import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import type { FacetValue } from '@/types/api';

type FacetKind = 'users' | 'channels' | 'domains';

const FETCHERS: Record<FacetKind, (signal?: AbortSignal) => Promise<{ items: FacetValue[] }>> = {
  users: (signal) => apiClient.listUsers(signal),
  channels: (signal) => apiClient.listChannels(signal),
  domains: (signal) => apiClient.listDomains(signal),
};

/**
 * Facet lists power the filter dropdowns. `linkCount` counts DISTINCT links — it is not a
 * share count, and must never be presented as one.
 */
export function useFacetQuery(kind: FacetKind): UseQueryResult<FacetValue[], Error> {
  return useQuery<FacetValue[], Error>({
    queryKey: queryKeys.facet(kind),
    queryFn: async ({ signal }) => {
      const response = await FETCHERS[kind](signal);
      return [...response.items].sort(
        (a, b) => b.linkCount - a.linkCount || a.label.localeCompare(b.label),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
